import { useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

// Projections (part 120): read models folded from the append-only audit stream. The screen
// shows the checkpoint each projection has reached and the snapshot produced by its last
// rebuild. Rebuilding is a full replay from seq 1 — never an incremental patch — so the
// numbers can always be checked against the source of truth.

interface ProjectionCard {
  name: string
  last_seq: number
  updated_at: string | null
  snapshot_built_at: string | null
}

interface ProjectionList {
  projections: ProjectionCard[]
}

interface CountRow {
  key: string
  count: number
}

interface FindingSnapshot {
  total: number
  by_severity: CountRow[]
  by_rule: CountRow[]
  skipped: number
}

interface DayRow {
  day: string
  count: number
}

interface ActivitySnapshot {
  total: number
  days: DayRow[]
}

interface SnapshotView {
  id: string
  name: string
  built_at: string
  payload: FindingSnapshot | ActivitySnapshot
}

interface RebuildResult extends ProjectionCard {
  events_applied: number
  baseline: number
  duration_ms: number
  snapshot: SnapshotView
}

const META: Record<string, { title: string; detail: string }> = {
  finding_counts: {
    title: 'ข้อสังเกตตามระดับและกฎ',
    detail: 'นับข้อสังเกตทั้งหมด แยกตามระดับความรุนแรงและรหัสกฎที่ตรวจพบ',
  },
  audit_activity: {
    title: 'กิจกรรมในบันทึกตรวจสอบ',
    detail: 'จำนวนเหตุการณ์รายวันตามเวลาไทย ใช้เป็นกราฟปริมาณงานของระบบ',
  },
}

const sevLabel: Record<string, string> = {
  critical: 'วิกฤต',
  high: 'สูง',
  medium: 'กลาง',
  low: 'ต่ำ',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function isFindingSnapshot(payload: unknown): payload is FindingSnapshot {
  return typeof payload === 'object' && payload !== null && 'by_severity' in payload
}

function FindingPreview({ snap }: { snap: FindingSnapshot }) {
  const maxRule = Math.max(1, ...snap.by_rule.map((r) => r.count))
  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div className="card-label">ข้อสังเกตทั้งหมด</div>
          <div className="stat-value" style={{ fontSize: 28 }}>
            {snap.total}
            <span className="stat-unit">รายการ</span>
          </div>
        </div>
        <div className="dim" style={{ fontSize: 11.5, textAlign: 'right' }}>
          baseline ที่ไม่มีเหตุการณ์สร้าง {snap.skipped} รายการ
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: 16, gap: 18 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>ตามระดับ</div>
          <table className="table">
            <tbody>
              {snap.by_severity.map((row) => (
                <tr key={row.key}>
                  <td>{sevLabel[row.key] ?? row.key}</td>
                  <td className="num">{row.count}</td>
                </tr>
              ))}
              {snap.by_severity.length === 0 && (
                <tr><td colSpan={2}><span className="dim">ยังไม่มีข้อมูล</span></td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>ตามกฎ (สูงสุด 8)</div>
          <div className="stack" style={{ gap: 8 }}>
            {snap.by_rule.slice(0, 8).map((row) => (
              <div key={row.key}>
                <div className="row" style={{ justifyContent: 'space-between', fontSize: 11.5 }}>
                  <span>{row.key}</span>
                  <span className="num">{row.count}</span>
                </div>
                <span className="bar" style={{ marginTop: 4 }}>
                  <i style={{ width: `${(row.count / maxRule) * 100}%` }} />
                </span>
              </div>
            ))}
            {snap.by_rule.length === 0 && <span className="dim" style={{ fontSize: 12 }}>ยังไม่มีข้อมูล</span>}
          </div>
        </div>
      </div>
    </>
  )
}

function ActivityPreview({ snap }: { snap: ActivitySnapshot }) {
  const recent = snap.days.slice(-10)
  const max = Math.max(1, ...recent.map((d) => d.count))
  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div className="card-label">เหตุการณ์ทั้งหมด</div>
          <div className="stat-value" style={{ fontSize: 28 }}>
            {snap.total}
            <span className="stat-unit">เหตุการณ์</span>
          </div>
        </div>
        <div className="dim" style={{ fontSize: 11.5 }}>{snap.days.length} วันที่มีกิจกรรม</div>
      </div>

      <div className="row" style={{ alignItems: 'flex-end', gap: 10, height: 96, marginTop: 18 }}>
        {recent.map((d) => (
          <div key={d.day} style={{ flex: 1, textAlign: 'center' }}>
            <div title={`${d.count} เหตุการณ์`} style={{
              height: `${Math.max(4, (d.count / max) * 68)}px`,
              background: 'var(--color-gold)', opacity: 0.7, borderRadius: 2,
              margin: '0 auto', width: 18,
            }} />
            <div className="dim" style={{ fontSize: 9.5, marginTop: 6 }}>{d.day.slice(5)}</div>
          </div>
        ))}
        {recent.length === 0 && <span className="dim" style={{ fontSize: 12 }}>ยังไม่มีเหตุการณ์</span>}
      </div>
    </>
  )
}

export function Projections() {
  const qc = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['projections'],
    queryFn: () => api<ProjectionList>('/v1/projections'),
    staleTime: 10_000,
  })

  const cards = list.data?.projections ?? []

  // Snapshots are fetched separately so the list stays cheap; a projection that was never
  // rebuilt has no snapshot and its 404 is simply "not previewed", not a page error.
  const snapshots = useQueries({
    queries: cards.map((card) => ({
      queryKey: ['projection-snapshot', card.name],
      queryFn: () => api<SnapshotView>(`/v1/projections/${card.name}/snapshot`),
      retry: false,
      staleTime: 10_000,
    })),
  })

  const rebuild = useMutation({
    mutationFn: (name: string) => apiWrite<RebuildResult>(`/v1/projections/${name}/rebuild`, 'POST'),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['projections'] })
      void qc.invalidateQueries({ queryKey: ['projection-snapshot'] })
      const label = META[res.name]?.title ?? res.name
      setNotice(
        `สร้าง ${label} ใหม่จากเหตุการณ์ ${res.events_applied} รายการ` +
        (res.baseline > 0 ? ` พร้อม baseline ${res.baseline} แถว` : '') +
        ` ถึง seq ${res.last_seq} ใช้เวลา ${res.duration_ms} ms`,
      )
    },
  })

  const latestSeq = cards.reduce((max, c) => Math.max(max, c.last_seq), 0)
  const built = cards
    .map((c) => c.snapshot_built_at)
    .filter((v): v is string => Boolean(v))
    .sort()
    .at(-1)

  return (
    <>
      <PageHead
        title="โมเดลอ่านที่สร้างใหม่ได้"
        sub="ทุกตัวเลขในหน้านี้คำนวณจากการไล่อ่าน event log ตั้งแต่ต้นใหม่ทั้งหมด ไม่ใช่การแก้ทีละรายการ · ใช้ตรวจสอบว่าข้อมูลสรุปตรงกับต้นทาง"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{cards.length} projection</span>
        <span className="chip">ถึง seq {latestSeq.toLocaleString('th-TH')}</span>
        <span className="chip">{built ? `snapshot ล่าสุด ${thaiDateTime(built)}` : 'ยังไม่มี snapshot'}</span>
      </div>

      {notice ? (
        <div role="status" style={{
          marginBottom: 16, padding: '10px 14px', fontSize: 12.5, lineHeight: 1.7,
          border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
          color: 'var(--color-emerald-soft)',
        }}>
          {notice}
        </div>
      ) : null}

      {rebuild.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(rebuild.error, 'สร้าง projection ใหม่ไม่สำเร็จ')}
        </div>
      ) : null}

      {list.isLoading ? (
        <div className="grid-2"><Skel h={320} /><Skel h={320} /></div>
      ) : list.isError ? (
        <div className="form-error">{errorText(list.error, 'โหลดรายการ projection ไม่สำเร็จ')}</div>
      ) : cards.length === 0 ? (
        <Empty title="ยังไม่มี projection" body="ระบบยังไม่ได้ลงทะเบียน read model ใด ๆ" />
      ) : (
        <div className="grid-2 stagger">
          {cards.map((card, i) => {
            const meta = META[card.name]
            const snap = snapshots[i]?.data
            const pending = rebuild.isPending && rebuild.variables === card.name
            return (
              <div key={card.name} className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div className="card-label">{meta?.title ?? card.name}</div>
                    <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>{card.name}</div>
                  </div>
                  <span className="chip">{card.last_seq > 0 ? 'พร้อมใช้' : 'ยังไม่สร้าง'}</span>
                </div>

                <p className="dim" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.7 }}>
                  {meta?.detail ?? 'โมเดลอ่านที่คำนวณจากการไล่ event log'}
                </p>

                <div className="row" style={{ gap: 24, marginTop: 14, marginBottom: 14 }}>
                  <div>
                    <div className="eyebrow">ถึง seq</div>
                    <div className="mono" style={{ fontSize: 15, marginTop: 3 }}>{card.last_seq.toLocaleString('th-TH')}</div>
                  </div>
                  <div>
                    <div className="eyebrow">ปรับปรุง</div>
                    <div style={{ fontSize: 12.5, marginTop: 3 }}>
                      {card.updated_at ? thaiDateTime(card.updated_at) : 'ยังไม่เคย rebuild'}
                    </div>
                  </div>
                </div>

                <div style={{
                  borderTop: '1px solid var(--color-hairline)', paddingTop: 14,
                  minHeight: 120,
                }}>
                  {snap && snap.payload ? (
                    isFindingSnapshot(snap.payload)
                      ? <FindingPreview snap={snap.payload} />
                      : <ActivityPreview snap={snap.payload} />
                  ) : (
                    <div className="dim" style={{ fontSize: 12, lineHeight: 1.8 }}>
                      ยังไม่มี snapshot ของ projection นี้ กดปุ่มด้านล่างเพื่อไล่อ่านเหตุการณ์ทั้งหมดและสร้างผลลัพธ์แรก
                    </div>
                  )}
                </div>

                <div className="row" style={{ justifyContent: 'space-between', marginTop: 16 }}>
                  <div className="dim" style={{ fontSize: 11 }}>
                    {snap ? `snapshot ${snap.id.slice(0, 8)} · ${thaiDateTime(snap.built_at)}` : 'snapshot จะถูกเก็บแบบเพิ่มอย่างเดียว'}
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={rebuild.isPending}
                    onClick={() => { setNotice(null); rebuild.mutate(card.name) }}
                  >
                    {pending ? 'กำลังไล่อ่าน…' : 'สร้างใหม่'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="dim" style={{ marginTop: 16, fontSize: 11.5, lineHeight: 1.8 }}>
        การ rebuild จะไม่ลบ snapshot เก่า และจะเขียน checkpoint ใหม่ก็ต่อเมื่อไล่อ่านจนจบเท่านั้น
        หากอ่านเหตุการณ์ไม่ครบ ระบบจะคงผลลัพธ์เดิมไว้เพื่อไม่ให้หน้ารายงานแสดงตัวเลขที่ขาดหาย
      </div>
    </>
  )
}
